import { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { supabase } from "../supabaseClient";
import moment from "moment-timezone";
import MicButton from "./common/MicButton";
import TaskAssistantChat from "./common/TaskAssistantChat";
import ChatAssistantInput from "./common/ChatAssistantInput";

const ScheduledTasksModal = ({ show, onHide, isDarkMode, userId }) => {
  const [tasks, setTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  // Edit states
  const [editingTask, setEditingTask] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPrompt, setEditPrompt] = useState("");
  const [editTime, setEditTime] = useState("");
  const [editScheduleType, setEditScheduleType] = useState("");
  const [saving, setSaving] = useState(false);

  // Chat assistant states
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatWidthPercent, setChatWidthPercent] = useState(70);
  const [isResizing, setIsResizing] = useState(false);
  const chatAssistantRef = useRef(null);
  const modalBodyRef = useRef(null);

  // Resize handler for chat width
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !modalBodyRef.current) return;
    const modalBody = modalBodyRef.current;
    const modalRect = modalBody.getBoundingClientRect();
    const mouseX = e.clientX - modalRect.left;
    const modalWidth = modalRect.width;
    const chatWidth = modalWidth - mouseX;
    let newPercent = (chatWidth / modalWidth) * 100;
    newPercent = Math.max(20, Math.min(70, newPercent));
    setChatWidthPercent(newPercent);
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Fetch scheduled tasks from Supabase
  const fetchTasks = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("scheduled_tasks")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching scheduled tasks:", error);
      } else {
        setTasks(data || []);
      }
    } catch (error) {
      console.error("Error fetching scheduled tasks:", error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (!show || !userId) return;

    fetchTasks();

    const channel = supabase
      .channel(`scheduled_tasks_${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "scheduled_tasks",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          if (payload.eventType === "INSERT") {
            setTasks((prev) => [payload.new, ...prev]);
          } else if (payload.eventType === "UPDATE") {
            setTasks((prev) =>
              prev.map((t) => (t.id === payload.new.id ? payload.new : t))
            );
          } else if (payload.eventType === "DELETE") {
            setTasks((prev) => prev.filter((t) => t.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [show, userId, fetchTasks]);

  // Toggle task status
  const toggleTaskStatus = async (task) => {
    const newStatus = task.status === "active" ? "paused" : "active";
    try {
      const { error } = await supabase
        .from("scheduled_tasks")
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq("id", task.id);

      if (error) {
        console.error("Error updating task status:", error);
      }
    } catch (error) {
      console.error("Error updating task status:", error);
    }
  };

  // Delete task
  const deleteTask = async (taskId) => {
    try {
      const { error } = await supabase
        .from("scheduled_tasks")
        .delete()
        .eq("id", taskId);

      if (error) {
        console.error("Error deleting task:", error);
      } else {
        // Update local state immediately after successful deletion
        setTasks((prev) => prev.filter((t) => t.id !== taskId));
      }
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting task:", error);
    }
  };

  // Start editing a task
  const startEditing = (task) => {
    setEditingTask(task.id);
    setEditTitle(task.title || "");
    setEditDescription(task.description || "");
    setEditPrompt(task.prompt || "");
    setEditTime(task.scheduled_time ? task.scheduled_time.substring(0, 5) : "");
    setEditScheduleType(task.schedule_type || "daily");
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingTask(null);
    setEditTitle("");
    setEditDescription("");
    setEditPrompt("");
    setEditTime("");
    setEditScheduleType("");
  };

  // Save edited task
  const saveTask = async (taskId) => {
    setSaving(true);
    try {
      const updateData = {
        title: editTitle,
        description: editDescription,
        prompt: editPrompt,
        scheduled_time: editTime ? `${editTime}:00` : null,
        schedule_type: editScheduleType,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("scheduled_tasks")
        .update(updateData)
        .eq("id", taskId);

      if (error) {
        console.error("Error updating task:", error);
      } else {
        cancelEditing();
      }
    } catch (error) {
      console.error("Error updating task:", error);
    } finally {
      setSaving(false);
    }
  };

  // Format schedule for display
  const formatSchedule = (task) => {
    if (!task.schedule_type) return "Unknown schedule";

    const time = task.scheduled_time
      ? moment(task.scheduled_time, "HH:mm:ss").format("h:mm A")
      : "";

    switch (task.schedule_type) {
      case "daily":
        return `Every day at ${time}`;
      case "weekly":
        const days = task.recurrence_rule?.days_of_week?.join(", ") || "Sunday";
        return `Every ${days} at ${time}`;
      case "monthly":
        const dayOfMonth = task.recurrence_rule?.day_of_month || 1;
        return `Monthly on day ${dayOfMonth} at ${time}`;
      case "once":
        const date = task.scheduled_date
          ? moment(task.scheduled_date).format("MMM D, YYYY")
          : "";
        return `Once on ${date} at ${time}`;
      default:
        return task.schedule_type;
    }
  };

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return "Not scheduled";
    return moment(dateStr).fromNow();
  };

  // Filter tasks
  const filteredTasks = tasks.filter((task) => {
    const matchesSearch =
      task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || task.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Toggle expand item
  const toggleExpand = (taskId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  // Helper function to get card classes
  const getCardClasses = (status) => {
    const baseClass = "st-card";
    const themeClass = isDarkMode ? "st-card-dark" : "st-card-light";
    const statusClass = status === "active"
      ? "st-card-active"
      : status === "paused"
      ? "st-card-paused"
      : "st-card-completed";
    return `${baseClass} ${themeClass} ${statusClass}`;
  };

  // Helper function to get status badge classes
  const getStatusBadgeClasses = (status) => {
    const baseClass = "st-status-badge";
    const statusClass = status === "active"
      ? "st-status-badge-active"
      : status === "paused"
      ? "st-status-badge-paused"
      : "st-status-badge-completed";
    return `${baseClass} ${statusClass}`;
  };

  // Helper function to get status dot classes
  const getStatusDotClasses = (status) => {
    const baseClass = "st-status-dot";
    const statusClass = status === "active"
      ? "st-status-dot-active"
      : status === "paused"
      ? "st-status-dot-paused"
      : "st-status-dot-completed";
    return `${baseClass} ${statusClass}`;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      contentClassName="border-0"
      style={{ zIndex: 1060 }}
      className="task-popup export-popup"
      dialogClassName="scheduled-tasks-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-calendar-check st-title-icon"></i>
          <span>Scheduled Tasks</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        ref={modalBodyRef}
        className="export-more"
        style={{
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'row',
          height: '70vh',
          padding: 0,
        }}
      >
        {/* Resize Handle */}
        {!isChatMinimized && (
          <div
            onMouseDown={handleResizeStart}
            style={{
              width: '14px',
              height: '100%',
              background: 'var(--border-color, #d5e5e3)',
              cursor: 'col-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              order: 1,
              position: 'relative',
              zIndex: 10,
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--teal-bg, #b8d4d0)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--border-color, #d5e5e3)'}
          >
            <i className="bi bi-grip-vertical" style={{ fontSize: '1rem', color: 'var(--text, #666)', opacity: 0.6 }}></i>
          </div>
        )}

        {/* Chat Assistant Panel - RIGHT SIDE */}
        <div style={{
          width: isChatMinimized ? '0px' : `${chatWidthPercent}%`,
          minWidth: isChatMinimized ? '0px' : '280px',
          maxWidth: isChatMinimized ? '0px' : '70%',
          height: '100%',
          transition: isResizing ? 'none' : 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          borderLeft: isChatMinimized ? 'none' : 'none',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          position: 'relative',
          background: isChatMinimized ? 'transparent' : '#FBF9FA',
          overflow: 'visible',
          order: 2
        }}>
          <TaskAssistantChat
            ref={chatAssistantRef}
            isDarkMode={isDarkMode}
            onToggle={(minimized) => setIsChatMinimized(minimized)}
            showInput={false}
            externalInput={chatInput}
            onExternalInputChange={setChatInput}
            contextType="scheduled_tasks"
            userId={userId}
            onItemsUpdated={() => {
              fetchTasks();
            }}
          />
        </div>

        {/* Main Content - LEFT SIDE */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: '16px',
          order: 0,
        }}>
        {/* Search and Filter */}
        <div className="d-flex gap-2 mb-3">
          <Form.Control
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`flex-grow-1 ${isDarkMode ? "st-input-dark" : "st-input-light"}`}
          />
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`st-filter-select ${isDarkMode ? "st-input-dark" : "st-input-light"}`}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
          </Form.Select>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {/* Empty state */}
        {!loading && filteredTasks.length === 0 && (
          <div className="text-center py-5">
            <i
              className={`bi bi-calendar-x st-empty-icon ${isDarkMode ? "st-empty-icon-dark" : "st-empty-icon-light"}`}
            ></i>
            <p className={`mt-3 ${isDarkMode ? "st-empty-text-dark" : "st-empty-text-light"}`}>
              {searchTerm ? "No matching tasks found" : "No scheduled tasks yet"}
            </p>
            <small className={isDarkMode ? "st-empty-subtext-dark" : "st-empty-subtext-light"}>
              Ask Zoomey to create a scheduled task like "Send me a daily morning brief"
            </small>
          </div>
        )}

        {/* Task List */}
        {!loading &&
          filteredTasks.map((task) => (
            <div
              key={task.id}
              className={getCardClasses(task.status)}
            >
              {/* Edit Mode */}
              {editingTask === task.id ? (
                <div className="edit-form">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label className="st-form-label">Title</Form.Label>
                        <Form.Control
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className={isDarkMode ? "st-input-dark" : "st-input-light"}
                          placeholder="Task title"
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-3">
                      <Form.Group>
                        <Form.Label className="st-form-label">Schedule</Form.Label>
                        <Form.Select
                          value={editScheduleType}
                          onChange={(e) => setEditScheduleType(e.target.value)}
                          className={isDarkMode ? "st-input-dark" : "st-input-light"}
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="once">Once</option>
                        </Form.Select>
                      </Form.Group>
                    </div>
                    <div className="col-md-3">
                      <Form.Group>
                        <Form.Label className="st-form-label">Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={editTime}
                          onChange={(e) => setEditTime(e.target.value)}
                          className={isDarkMode ? "st-input-dark" : "st-input-light"}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-12">
                      <Form.Group>
                        <Form.Label className="st-form-label">Description</Form.Label>
                        <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className={isDarkMode ? "st-input-dark" : "st-input-light"}
                            placeholder="Task description"
                            style={{ flex: 1 }}
                          />
                          <MicButton
                            onTranscript={(text) => setEditDescription(prev => prev ? `${prev} ${text}` : text)}
                            size="sm"
                          />
                        </div>
                      </Form.Group>
                    </div>
                    <div className="col-12">
                      <Form.Group>
                        <Form.Label className="st-form-label">Prompt</Form.Label>
                        <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={editPrompt}
                            onChange={(e) => setEditPrompt(e.target.value)}
                            className={`st-prompt-input ${isDarkMode ? "st-input-dark" : "st-input-light"}`}
                            placeholder="What should happen when this task runs?"
                            style={{ flex: 1 }}
                          />
                          <MicButton
                            onTranscript={(text) => setEditPrompt(prev => prev ? `${prev} ${text}` : text)}
                            size="sm"
                          />
                        </div>
                      </Form.Group>
                    </div>
                  </div>
                  <div className="d-flex gap-2 mt-3 justify-content-end">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={cancelEditing}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-sm st-save-btn"
                      onClick={() => saveTask(task.id)}
                      disabled={saving || !editTitle}
                    >
                      {saving ? <Spinner size="sm" /> : "Save Changes"}
                    </button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <>
                  <div className="d-flex justify-content-between align-items-start gap-3">
                    {/* Left: Icon + Content */}
                    <div className="d-flex gap-3 flex-grow-1 st-clickable" onClick={() => toggleExpand(task.id)}>
                      {/* Schedule Type Icon */}
                      <div className={`st-icon-container ${isDarkMode ? "st-icon-container-dark" : "st-icon-container-light"}`}>
                        <i
                          className={`bi ${
                            task.schedule_type === "daily"
                              ? "bi-sun-fill"
                              : task.schedule_type === "weekly"
                              ? "bi-calendar-week-fill"
                              : task.schedule_type === "monthly"
                              ? "bi-calendar-month-fill"
                              : "bi-calendar-event-fill"
                          }`}
                        ></i>
                      </div>

                      {/* Content */}
                      <div className="flex-grow-1 st-min-w-0">
                        {/* Title Row */}
                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                          <strong className="st-task-title">
                            {task.title}
                          </strong>
                          <span className={getStatusBadgeClasses(task.status)}>
                            <span className={getStatusDotClasses(task.status)}></span>
                            {task.status}
                          </span>
                        </div>

                        {/* Schedule Info */}
                        <div className={`st-schedule-info ${isDarkMode ? "st-schedule-info-dark" : "st-schedule-info-light"}`}>
                          <i className="bi bi-clock st-icon-xs"></i>
                          {formatSchedule(task)}
                        </div>

                        {/* Info Chips */}
                        <div className="d-flex gap-2 flex-wrap">
                          {task.next_run_at && task.status === "active" && (
                            <span className="st-info-chip st-info-chip-next">
                              <i className="bi bi-lightning-fill st-icon-sm"></i>
                              Next: {formatRelativeTime(task.next_run_at)}
                            </span>
                          )}

                          {task.last_run_at && (
                            <span className={`st-info-chip ${isDarkMode ? "st-info-chip-dark" : "st-info-chip-light"}`}>
                              <i className="bi bi-check-circle-fill st-icon-sm st-success-icon"></i>
                              Last: {formatRelativeTime(task.last_run_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="d-flex gap-2 align-items-center st-flex-shrink-0">
                      {/* Toggle Switch */}
                      <Form.Check
                        type="switch"
                        checked={task.status === "active"}
                        onChange={() => toggleTaskStatus(task)}
                        className="custom-switch st-switch-margin"
                      />

                      {/* Edit Button */}
                      <button
                        className={`st-action-btn ${isDarkMode ? "st-action-btn-edit-dark" : "st-action-btn-edit-light"}`}
                        onClick={() => startEditing(task)}
                        title="Edit task"
                      >
                        <i className="bi bi-pencil-fill"></i>
                      </button>

                      {/* Delete Button */}
                      {deleteConfirm === task.id ? (
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm st-delete-confirm-btn st-delete-confirm-yes"
                            onClick={() => deleteTask(task.id)}
                          >
                            Delete
                          </button>
                          <button
                            className={`btn btn-sm st-delete-confirm-btn ${isDarkMode ? "st-delete-confirm-no-dark" : "st-delete-confirm-no-light"}`}
                            onClick={() => setDeleteConfirm(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className={`st-action-btn ${isDarkMode ? "st-action-btn-delete-dark" : "st-action-btn-delete-light"}`}
                          onClick={() => setDeleteConfirm(task.id)}
                          title="Delete task"
                        >
                          <i className="bi bi-trash-fill"></i>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedItems[task.id] && (
                    <div className={`st-expanded-section ${isDarkMode ? "st-expanded-section-dark" : "st-expanded-section-light"}`}>
                      {task.description && (
                        <div className="mb-3">
                          <div className={`st-section-label ${isDarkMode ? "st-section-label-dark" : "st-section-label-light"}`}>
                            Description
                          </div>
                          <p className={`st-description-text ${isDarkMode ? "st-description-text-dark" : "st-description-text-light"}`}>
                            {task.description}
                          </p>
                        </div>
                      )}

                      {task.prompt && (
                        <div className="mb-3">
                          <div className={`st-section-label ${isDarkMode ? "st-section-label-dark" : "st-section-label-light"}`}>
                            Prompt
                          </div>
                          <p className={`st-prompt-box ${isDarkMode ? "st-prompt-box-dark" : "st-prompt-box-light"}`}>
                            {task.prompt}
                          </p>
                        </div>
                      )}

                      <div className="d-flex gap-3 flex-wrap">
                        <span className={`st-info-chip ${isDarkMode ? "st-info-chip-dark" : "st-info-chip-light"}`}>
                          <i className="bi bi-globe2 st-icon-xs"></i>
                          {task.timezone || "UTC"}
                        </span>
                        <span className={`st-info-chip ${isDarkMode ? "st-info-chip-dark" : "st-info-chip-light"}`}>
                          <i className="bi bi-calendar-plus st-icon-xs"></i>
                          Created: {moment(task.created_at).format("MMM D, YYYY")}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px' }}>
        <ChatAssistantInput
          chatAssistantRef={chatAssistantRef}
          inputValue={chatInput}
          onInputChange={setChatInput}
          onClearChat={() => chatAssistantRef.current?.clearChat()}
          autoFocus={true}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default ScheduledTasksModal;
